/**
 * Replay GameMessages into current engine state.
 *
 * Callers feed incoming `GameMessage`s from the MLS-decrypted stream through
 * `reduce`. The reducer keeps a running state keyed by engineId; duplicate
 * or out-of-order `moveIndex` values are rejected as drift.
 */
import type { UserId } from '@chat/protocol';
import type { GameMessage } from '../client/payload.js';
import type { GameEngine } from './engine.js';

export interface GameSessionState<State> {
  readonly engineId: string;
  readonly state: State;
  readonly players: readonly UserId[];
  readonly nextMoveIndex: number;
}

export type GameSessionError =
  | { readonly kind: 'unknown-engine'; readonly engineId: string }
  | { readonly kind: 'missing-players' }
  | { readonly kind: 'out-of-order'; readonly expected: number; readonly received: number }
  | { readonly kind: 'invalid-move'; readonly reason: string }
  | { readonly kind: 'invalid-message' };

export function startSession<State, Move>(
  engine: GameEngine<State, Move>,
  message: GameMessage,
  _sender: UserId,
): GameSessionState<State> | GameSessionError {
  if (message.engineId !== engine.id) {
    return { kind: 'unknown-engine', engineId: message.engineId };
  }
  if (message.moveIndex !== 0) {
    return { kind: 'out-of-order', expected: 0, received: message.moveIndex };
  }
  if (!message.players || message.players.length < engine.minPlayers) {
    return { kind: 'missing-players' };
  }
  // Drop any players above the max so 3+-member groups work with 2-player games.
  const players = message.players.slice(0, engine.maxPlayers);
  let state: State;
  try {
    state = engine.initialState(players);
  } catch (err) {
    return {
      kind: 'invalid-move',
      reason: err instanceof Error ? err.message : 'init failed',
    };
  }
  // moveIndex 0 is a bootstrap message: it establishes the session and the
  // player list but carries no move. Real moves start at index 1.
  return {
    engineId: engine.id,
    state,
    players,
    nextMoveIndex: 1,
  };
}

export function applyMessage<State, Move>(
  engine: GameEngine<State, Move>,
  session: GameSessionState<State>,
  message: GameMessage,
  sender: UserId,
): GameSessionState<State> | GameSessionError {
  if (message.engineId !== engine.id || message.engineId !== session.engineId) {
    return { kind: 'unknown-engine', engineId: message.engineId };
  }
  if (message.moveIndex !== session.nextMoveIndex) {
    return {
      kind: 'out-of-order',
      expected: session.nextMoveIndex,
      received: message.moveIndex,
    };
  }
  const validation = engine.validateMove(session.state, message.move as Move, sender);
  if (validation) return { kind: 'invalid-move', reason: validation };
  const nextState = engine.applyMove(session.state, message.move as Move, sender);
  return {
    ...session,
    state: nextState,
    nextMoveIndex: session.nextMoveIndex + 1,
  };
}
